import {
    wweBundle_wweBundle_options_options,
    wweBundle_wweBundle_target_configurable_ConfigurableProduct,
    wweBundle_wweBundle_target_configurable_ConfigurableProduct_variants,
} from 'src/queries/__generated__/wweBundle';
import {
    GqlOption,
    GqlOptionValue,
    GqlVariantAttr,
    optionFromQuery,
    optionValueFromQuery,
} from 'src/components/ProductDetail/utils/product-options';
import { ProductConfigOptions, ProductConfigOptionsValues } from 'src/components/ProductDetail/ProductOptions';
import { BaseVariant, ConfigurableAttributeOption } from 'src/components/ProductDetail/utils/product-detail.types';
import { StockStatus } from 'src/types/global-types';
import { appendMap } from 'src/util/appendMap';
import { get } from 'es-toolkit/compat';

import { LabelsWithOneBoundleImageProps } from '../BundleOptionsProvider';

/**
 * This is "sku": option_id
 */
export type BundleOption = {
    sku: string;
    simpleProductId: number;
    simpleProductSku: string | null;
    optionItemID: number;
};

export type BundleOptions = {
    optionId: number;
    items: Record<string, BundleOption>;
};

export const EMPTY_OPTIONS: BundleOptions = { items: {}, optionId: -1 };
export const EMPTY_OPTION: BundleOption = {
    optionItemID: -1,
    simpleProductId: -1,
    simpleProductSku: null,
    sku: 'unknown',
};

export function bundleOptionsFromQuery<T extends Omit<wweBundle_wweBundle_options_options, '__typename'>>(
    option_id: number | null | undefined,
    options: (T | null)[] | null | undefined,
): BundleOptions {
    if (!options) return EMPTY_OPTIONS;
    const optionId = option_id || -1;
    const output: BundleOptions = { optionId: optionId, items: {} };
    options.forEach((optionItem) => {
        if (!optionItem) return;
        const { product, id } = optionItem;
        if (!product) return EMPTY_OPTION;
        output.items[product.sku!] = {
            sku: product.sku!,
            simpleProductId: product.id!,
            simpleProductSku: product.sku!,
            optionItemID: id!,
        };
    });
    return output;
}

function stockStatusFromQuery(stock_status: string | null, only_x_left_in_stock: number | null): StockStatus {
    if (stock_status === 'OUT_OF_STOCK') {
        return StockStatus.OutOfStock;
    }
    if (only_x_left_in_stock !== null) {
        return StockStatus.LowStock;
    }
    return StockStatus.InStock;
}

type ArrayOfLabelsAndBoundleImagesProps = {
    label: string | null;
    image: string | null;
}[];

/**
 * @param item
 * @param bundleOptions
 */
export function bundleOptionsAndVariantsWithStock(
    item: wweBundle_wweBundle_target_configurable_ConfigurableProduct,
    bundleOptions: BundleOptions,
): {
    options: ProductConfigOptions[];
    variants: BaseVariant[];
    labelsWithOneBoundleImage: LabelsWithOneBoundleImageProps;
} {
    const variants: BaseVariant[] = [];
    let labelsWithOneBoundleImage: LabelsWithOneBoundleImageProps = [];
    const arrayOfLabelsAndBoundleImages: ArrayOfLabelsAndBoundleImagesProps = [];

    /**
     * Keep track of valid
     */
    const { append, values } = appendMap<string, number>();

    (item.variants as GqlBundleVariant[]).forEach((variant) => {
        /**
         * Do nothing if this products sku does not exist in the bundle options
         * ^ this may be the case because not all configurable options will be supported
         */
        if (!bundleOptions.items[variant.product!.sku!]) return;

        /**
         * Check that at least 1 attribute exists
         */
        const attr = variant.attributes as GqlVariantAttr[];

        /**
         * If 1 attr, or the actual variant doesn't exist, do nothing.
         */
        if (!attr || attr.length === 0 || !variant.product) {
            return;
        }

        /**
         * Select the size_id & color_id.
         *
         * Note: this is an optimization since we know this project only has these 2 attributes.
         */
        const size_id = variant.attributes?.find((a) => a!.code === 'size')?.value_index ?? 0;
        const color_id = variant.attributes?.find((a) => a!.code === 'color')?.value_index ?? 0;

        /**
         * Mark both as valid
         */
        append('size', size_id);
        append('color', color_id);

        /**
         * Add the variant
         */
        variants.push({
            id: variant.product.id!,
            sku: variant.product.sku!,
            attributes: variant.attributes as ConfigurableAttributeOption[],
            size_id: size_id,
            color_id: color_id,
            stock_status: stockStatusFromQuery(variant.product.stock_status, variant.product.only_x_left_in_stock),
            max_qty: variant.product.stock_qty,
        });

        // es-toolkit's `get` returns `unknown | undefined`, whereas lodash inferred
        // narrow string types for known paths. Coerce explicitly to keep the
        // downstream shape (`string | null`) intact.
        const label = (get(variant, 'attributes[0].label') ?? null) as string | null;
        const boundleImage = (get(variant, 'product.image.url') ?? null) as string | null;

        if (
            arrayOfLabelsAndBoundleImages.length <= 0 ||
            arrayOfLabelsAndBoundleImages.filter((obj) => obj.label === label).length === 0
        ) {
            arrayOfLabelsAndBoundleImages.push({
                label: label,
                image: boundleImage,
            });
        }

        const getLabelAndOneBundleImageFn = (
            arrayOfLabelsAndBoundleImages: ArrayOfLabelsAndBoundleImagesProps,
            key: string,
        ) => {
            return arrayOfLabelsAndBoundleImages?.reduce((rv, x) => {
                (rv[x[key]] = rv[x[key]] || []).push(x.image);
                return rv;
            }, {});
        };
        labelsWithOneBoundleImage = getLabelAndOneBundleImageFn(arrayOfLabelsAndBoundleImages, 'label');
    });

    function filter(value: GqlOptionValue, attr_code: string) {
        return Boolean(values.get(attr_code)?.has(value.value_index!));
    }

    const options: ProductConfigOptions[] = filterBundleOptions(item.configurable_options as GqlOption[], filter);

    return { options, variants, labelsWithOneBoundleImage };
}

type OptionFilterFn = (optionValue: GqlOptionValue, attribute_code: string) => boolean;

/**
 * @param options
 * @param filterOptionValue
 */
function filterBundleOptions(options: GqlOption[], filterOptionValue?: OptionFilterFn): ProductConfigOptions[] {
    const nextOptions: ProductConfigOptions[] = [];
    options.forEach((option) => {
        const values: ProductConfigOptionsValues[] = [];
        (option.values as GqlOptionValue[]).forEach((value) => {
            if (filterOptionValue) {
                if (filterOptionValue(value, option.attribute_code!)) {
                    values.push(optionValueFromQuery(value));
                }
            } else {
                values.push(optionValueFromQuery(value));
            }
        });
        /**
         * Dont allow an option at all if there are no valid variants
         */
        if (values.length === 0) {
            return;
        }

        nextOptions.push(optionFromQuery(option, values));
    });
    return nextOptions;
}

export type GqlBundleVariant = wweBundle_wweBundle_target_configurable_ConfigurableProduct_variants;
